import { useNavigate, useParams } from "react-router-dom";
import JumboAppTextField from "@jumbo/components/JumboAppTextField";

import { Form, Formik } from "formik";
import * as Yup from "yup";

import Stack from "@mui/material/Stack";
import LoadingButton from "@mui/lab/LoadingButton";
import { CircularProgress, useMediaQuery } from "@mui/material";
import { Card, CardContent, CardHeader } from "@mui/material";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import FormControl from "@mui/material/FormControl";
import Select from "@mui/material/Select";
import FormHelperText from "@mui/material/FormHelperText";
import React, { useEffect, useState } from "react";
import JumboPasswordField from "@jumbo/components/JumboFormik/JumboPasswordField";
import { useDispatch, useSelector } from "react-redux";
import { useSnackbar } from "notistack";
import { getRoleList } from "app/redux/slice/roleSlice";
import { createUser, editUser, updateUser } from "app/redux/slice/userSlice";
import { USERS } from "app/routes/constant";

const AddUser = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const roleData = useSelector((state) => state?.role);
  let { id } = useParams();
  const [user, setUser] = useState();
  const mobileView = useMediaQuery("(max-width:575px)");

  const validationSchema = Yup.object({
    name: Yup.string().required("Please enter name"),
    email: Yup.string()
      .email(
        "Please enter a valid email address in the format example@domain.com"
      )
      .required("Please enter email"),
    role: Yup.string().required("Please select a role"),
    mobileNumber: Yup.string()
      .required("mobile number is required.")
      .matches(
        /^(?:(?:\+[1-9]{1,4}[ \-]*)|(?:\([0-9]{2,3}\)[ \-]*)|(?:[0-9]{2,4}[ \-]*))*(?:\+[0-9]{1,4}[ \-]*)?[0-9]{3,4}[ \-]*[0-9]{3,4}$/,
        "Mobile number is not valid"
      ),
    password: id
      ? Yup.string()
      : Yup.string().required("The Password field is required"),
    confirmPassword: id
      ? Yup.string()
      : Yup.string()
          .required("The Confirm Password field is required")
          .oneOf([Yup.ref("password")], "Passwords does not match"),
  });

  useEffect(() => {
    dispatch(getRoleList());
    if (id) {
      dispatch(editUser(id))
        .unwrap()
        .then((response) => {
          if (response.status === 200) {
            setUser(response?.data?.data?.userDetail);
          } else {
            enqueueSnackbar(response.data?.responseMessage, {
              variant: "error",
            });
          }
        });
    }
  }, [dispatch, enqueueSnackbar, id]);

  return (
    <>
      <Card>
        <CardHeader title={id ? "Update User" : "Add User"} />
        <CardContent>
          <Formik
            initialValues={{
              name: user?.name || "",
              email: user?.email || "",
              role: user?.roleId?._id || "",
              mobileNumber: user?.mobileNumber || "",
              password: "",
              confirmPassword: "",
            }}
            validationSchema={validationSchema}
            enableReinitialize
            onSubmit={async (data, { setSubmitting }) => {
              setSubmitting(true);
              const newUserData = {
                name: data?.name?.trim(),
                roleId: data?.role,
                email: data?.email,
                mobileNumber: data?.mobileNumber,
                colorCode: data?.colorCode,
              };
              if (!id) newUserData.password = data?.password;
              const response = await dispatch(
                id
                  ? updateUser({ id, data: newUserData })
                  : createUser(newUserData)
              ).unwrap();
              if (response.status === 200) {
                enqueueSnackbar(response.data?.responseMessage, {
                  variant: "success",
                });
                navigate(USERS);
                setSubmitting(false);
              } else {
                enqueueSnackbar(response.data?.responseMessage, {
                  variant: "error",
                });
              }
            }}
          >
            {({
              values,
              errors,
              touched,
              isSubmitting,
              handleChange,
              handleBlur,
              setFieldValue,
            }) => (
              <Form>
                <Stack
                  spacing={{ xs: 1, sm: 4 }}
                  direction="row"
                  sx={{ mb: 3 }}
                >
                  <JumboAppTextField fullWidth name="name" label="Name*" />
                  <JumboAppTextField
                    type="email"
                    fullWidth
                    name="email"
                    label="Email*"
                  />
                </Stack>
                <Stack
                  spacing={{ xs: 1, sm: 4 }}
                  direction="row"
                  sx={{ mb: 3 }}
                >
                  <FormControl
                    fullWidth
                    error={touched.role && Boolean(errors.role)}
                  >
                    <InputLabel id="role-label">Role</InputLabel>
                    <Select
                      name="role"
                      labelId="role-label"
                      id="role"
                      value={values.role}
                      label="Role"
                      onChange={handleChange}
                      onBlur={handleBlur}
                    >
                      {roleData?.list?.map((val) => {
                        return (
                          <MenuItem key={val._id} value={val._id}>
                            {val.name}
                          </MenuItem>
                        );
                      })}
                    </Select>
                    {touched.role && errors.role && (
                      <FormHelperText>{errors.role}</FormHelperText>
                    )}
                  </FormControl>
                  <JumboAppTextField
                    fullWidth
                    name="mobileNumber"
                    label="Phone Number*"
                  />
                </Stack>

                {!id && (
                  <Stack
                    spacing={{ xs: 1, sm: 4 }}
                    direction="row"
                    sx={{ mb: 3 }}
                  >
                    <JumboPasswordField
                      type="password"
                      fullWidth
                      name="password"
                      label="Password*"
                    />
                    <JumboPasswordField
                      type="password"
                      fullWidth
                      name="confirmPassword"
                      label="Confirm Password*"
                    />
                  </Stack>
                )}
                <Stack
                  spacing={{ xs: 1, sm: 2 }}
                  direction="row"
                  justifyContent="flex-end"
                  alignItems="flex-end"
                  sx={{ width: "100%", pt: { xs: 3, md: 0 } }}
                >
                  <LoadingButton
                    variant="outlined"
                    color="secondary"
                    size="large"
                    onClick={() => {
                      navigate(USERS);
                    }}
                    fullWidth={mobileView}
                  >
                    Cancel
                  </LoadingButton>
                  <LoadingButton
                    type="submit"
                    variant="contained"
                    size="large"
                    loading={isSubmitting}
                    loadingIndicator={<CircularProgress size={20} />}
                    fullWidth={mobileView}
                  >
                    Save
                  </LoadingButton>
                </Stack>
              </Form>
            )}
          </Formik>
        </CardContent>
      </Card>
    </>
  );
};

export default AddUser;
